import React, { useState, useEffect } from 'react';
import { KnowledgeFile, Category } from '../../types';
import { getFileIcon, getLanguageFromExtension, isMarkdownFile } from '../../utils/file-types';
import { getSignedUrl, getPublicUrl, uploadToR2 } from '../../config/storage';
import { supabase } from '../../config/supabase';
import { formatFileSize } from '../../utils/file-types';
import { useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import ReactPlayer from 'react-player';
import { domToCanvas } from 'modern-screenshot';
import { cn } from '../../utils/cn';
import { 
  Download, Trash2, Star,
  ArrowLeft, Check, X, Camera, Upload, Image as ImageIcon, 
  MoreHorizontal, Shield, Maximize2, Minimize2,
  ChevronRight, Info, Edit3, Copy, Link2,
  PanelRightOpen, PanelRightClose
} from 'lucide-react';
import { useFileStore } from '../../stores/file-store';
import dayjs from 'dayjs';
import { useI18n } from '../../i18n';
import { useCategoryStore } from '../../stores/category-store';
import { PERMISSION_LABELS, PERMISSION_COLORS } from '../../utils/permissions';
import FilePermissionModal from './FilePermissionModal';
import { showToast } from '../../utils/toast';
// 代码高亮和 Markdown 预览
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { SourceParseButton } from '@/price_intel';

// Set up PDF.js worker - 使用更稳定的 worker 配置
// 延迟设置 worker 以避免初始化时的竞争条件
if (typeof window !== 'undefined') {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
}

interface FileDetailsViewerProps {
  file: KnowledgeFile;
}

const FileDetailsViewer: React.FC<FileDetailsViewerProps> = ({ file: initialFile }) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { categories, fetchCategories } = useCategoryStore();
  const [file, setFile] = useState(initialFile);
  const { starFile, deleteFile, updateFile } = useFileStore();
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState(file.name);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfError, setPdfError] = useState<Error | null>(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  // 预览区域容器引用，用于计算动态高度
  const previewContainerRef = React.useRef<HTMLDivElement>(null);
  // 通用预览区域 ref，用于截取任何文件类型的预览截图
  const previewAreaRef = React.useRef<HTMLDivElement>(null);
  const [previewHeight, setPreviewHeight] = useState<number>(600);
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const FileIcon = getFileIcon(file.type);
  const pdfViewerRef = React.useRef<HTMLDivElement>(null);
  const videoRef = React.useRef<ReactPlayer>(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [isDesktop, setIsDesktop] = useState<boolean>(true);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  
  // 新增：侧边栏展开状态
  const [showInfoPanel, setShowInfoPanel] = useState(true);
  
  // 添加显示当前缩略图的状态
  const [currentThumbnailUrl, setCurrentThumbnailUrl] = useState<string | null>(null);
  // 预加载原图完成标记（用于先显示缩略图占位）
  const [isFullImageReady, setIsFullImageReady] = useState(false);
  // Office 文件预览 URL（使用微软 Office Online Viewer）
  const [officeViewerUrl, setOfficeViewerUrl] = useState<string | null>(null);
  const [officePreviewError, setOfficePreviewError] = useState(false);
  // 代码/文本文件内容
  const [codeContent, setCodeContent] = useState<string | null>(null);
  const [codeLoading, setCodeLoading] = useState(false);
  // 全屏预览模式
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fileCategoryRefs, setFileCategoryRefs] = useState<Array<{
    categoryId: string;
    fallbackPath: Category[];
  }>>([]);
  const [fileLocationLoading, setFileLocationLoading] = useState(false);
  
  // 在initialFile更新时同步更新本地状态
  useEffect(() => {
    if (initialFile) {
      const safeFile = {
        ...initialFile,
        tags: initialFile.tags || [],
        versions: initialFile.versions || []
      };
      setFile(safeFile);
    }
  }, [initialFile]);

  // 确保有分类数据用于生成面包屑
  useEffect(() => {
    if (!categories || categories.length === 0) {
      try { fetchCategories(); } catch {}
    }
  }, [categories, fetchCategories]);

  useEffect(() => {
    let isMounted = true;

    const loadFileLocations = async () => {
      if (!file.id) return;

      setFileLocationLoading(true);
      try {
        const resolveCategoryPath = async (category: any): Promise<Category[]> => {
          if (!category?.id) return [];

          const path: Category[] = [{
            id: category.id,
            name: category.name,
            parent_id: category.parent_id || undefined,
          } as Category];
          const visited = new Set<string>([category.id]);
          let parentId = category.parent_id as string | null | undefined;

          while (parentId && !visited.has(parentId) && path.length < 20) {
            const { data: parent, error: parentError } = await supabase
              .from('categories')
              .select('id, name, parent_id')
              .eq('id', parentId)
              .single();

            if (parentError || !parent) break;

            path.unshift({
              id: parent.id,
              name: parent.name,
              parent_id: parent.parent_id || undefined,
            } as Category);
            visited.add(parent.id);
            parentId = parent.parent_id;
          }

          return path;
        };

        const { data, error } = await supabase
          .from('file_categories')
          .select('category_id, category:categories(id, name, parent_id)')
          .eq('file_id', file.id);

        if (error) throw error;
        if (!isMounted) return;

        const refs = await Promise.all((data || [])
          .map(async (row: any) => ({
            categoryId: row.category_id as string,
            fallbackPath: await resolveCategoryPath(row.category),
          }))
        );

        if (!isMounted) return;
        setFileCategoryRefs(refs.filter((row) => Boolean(row.categoryId)));
      } catch (error) {
        console.error('获取文件所在文件夹失败:', error);
        if (isMounted) setFileCategoryRefs([]);
      } finally {
        if (isMounted) setFileLocationLoading(false);
      }
    };

    loadFileLocations();
    return () => { isMounted = false; };
  }, [file.id]);

  // 监听屏幕以判断是否为桌面端
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') return;
    const media = window.matchMedia('(min-width: 768px)');
    const listener = (e: MediaQueryListEvent) => {
      setIsDesktop(e.matches);
      // 移动端默认隐藏侧边栏
      if (!e.matches) setShowInfoPanel(false);
    };
    setIsDesktop(media.matches);
    if (!media.matches) setShowInfoPanel(false);
    try {
      media.addEventListener('change', listener);
      return () => media.removeEventListener('change', listener);
    } catch {
      media.addListener(listener);
      return () => media.removeListener(listener);
    }
  }, []);

  const categoryLookup = React.useMemo(() => {
    const map = new Map<string, Category>();
    const flatten = (nodes: Category[]) => {
      for (const node of nodes) {
        map.set(node.id, node);
        if (node.children && node.children.length > 0) {
          flatten(node.children as Category[]);
        }
      }
    };

    flatten((categories || []) as Category[]);
    return map;
  }, [categories]);

  const fileLocationPaths = React.useMemo(() => {
    return fileCategoryRefs
      .map(({ categoryId, fallbackPath }) => {
        const path: Category[] = [];
        const visited = new Set<string>();
        let current = categoryLookup.get(categoryId);

        while (current && !visited.has(current.id)) {
          path.unshift(current);
          visited.add(current.id);
          if (!current.parent_id) break;
          current = categoryLookup.get(current.parent_id);
        }

        return fallbackPath.length > path.length ? fallbackPath : path;
      })
      .filter((path) => path.length > 0);
  }, [categoryLookup, fileCategoryRefs]);

  const primaryCategoryPath = fileLocationPaths[0] || [];
  const primaryCategory = primaryCategoryPath[primaryCategoryPath.length - 1];
  const primaryFolderPathLabel = primaryCategoryPath.map((node) => node.name).join(' / ');
  const topBreadcrumbPath = primaryCategoryPath.length > 4
    ? primaryCategoryPath.slice(-4)
    : primaryCategoryPath;
  const currentFilePathLabel = primaryFolderPathLabel
    ? `${primaryFolderPathLabel} / ${file.name}`
    : file.name;

  // 加载 Office 文件预览 URL
  useEffect(() => {
    const loadOfficeViewerUrl = async () => {
      if (!file.storage_key) return;
      const officeTypes = ['document', 'spreadsheet', 'presentation'];
      if (officeTypes.includes(file.type)) {
        try {
          const fileUrl = await getPublicUrl(file.storage_key);
          const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
          setOfficeViewerUrl(viewerUrl);
          setOfficePreviewError(false);
        } catch (error) {
          console.error('Error generating Office viewer URL:', error);
          setOfficePreviewError(true);
        }
      }
    };
    loadOfficeViewerUrl();
  }, [file.storage_key, file.type]);

  // 加载代码/文本文件内容
  useEffect(() => {
    const loadCodeContent = async () => {
      if (!file.storage_key || file.type !== 'code') return;
      try {
        setCodeLoading(true);
        const url = await getSignedUrl(file.storage_key);
        const response = await fetch(url);
        const text = await response.text();
        if (text.length > 500000) {
          setCodeContent(text.slice(0, 500000) + '\n\n... (文件过大，仅显示前 500KB)');
        } else {
          setCodeContent(text);
        }
      } catch (error) {
        console.error('Error loading code content:', error);
        setCodeContent(null);
      } finally {
        setCodeLoading(false);
      }
    };
    loadCodeContent();
  }, [file.storage_key, file.type]);

  // 确保顶部工具栏初始化时创建文件输入元素
  useEffect(() => {
    if (!document.getElementById('file-thumbnail-upload')) {
      const input = document.createElement('input');
      input.id = 'file-thumbnail-upload';
      input.type = 'file';
      input.accept = 'image/*';
      input.style.display = 'none';
      input.addEventListener('change', (e) => {
        // @ts-ignore
        const files = e.target.files;
        if (files && files.length > 0) {
          const selectedFile = files[0];
          if (selectedFile.type.startsWith('image/')) {
            handleThumbnailUpload(selectedFile);
          } else {
            showToast(t('pleaseSelectValidImage'));
          }
        }
      });
      document.body.appendChild(input);
    }
  }, []);

  // 加载当前文件的缩略图
  useEffect(() => {
    const loadThumbnail = async () => {
      if (file.thumbnail_key) {
        try {
          const { data: latestFile, error: fetchError } = await supabase
            .from('files')
            .select('thumbnail_key, thumbnail_generated_at')
            .eq('id', file.id)
            .single();
          if (fetchError) {
            console.error('获取最新文件信息失败:', fetchError);
            setCurrentThumbnailUrl(null);
            return;
          }
          const thumbnailKey = latestFile.thumbnail_key;
          if (thumbnailKey) {
            const url = await getPublicUrl(thumbnailKey);
            setCurrentThumbnailUrl(url);
          } else {
            setCurrentThumbnailUrl(null);
          }
        } catch (error) {
          console.error('加载缩略图失败:', error);
          setCurrentThumbnailUrl(null);
        }
      } else {
        setCurrentThumbnailUrl(null);
      }
    };
    loadThumbnail();
  }, [file.id, file.thumbnail_key]);

  // ========== 所有原有的处理函数保持不变 ==========
  
  const handleCaptureVideoFrame = async () => {
    if (!videoRef.current || !file.storage_key) return;
    try {
      setIsCapturing(true);
      const videoElement = videoRef.current.getInternalPlayer() as HTMLVideoElement;
      videoElement.crossOrigin = 'anonymous';
      const canvas = document.createElement('canvas');
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      if (!videoElement.videoWidth || !videoElement.videoHeight) {
        await new Promise((resolve) => { videoElement.onloadedmetadata = resolve; });
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Failed to get canvas context');
      ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => { if (blob) resolve(blob); else reject(new Error('Failed to create blob')); }, 'image/jpeg', 0.85);
      });
      const uniqueId = Date.now().toString();
      const thumbnailKey = `thumbnails/${file.storage_key}/${uniqueId}-${file.name}`;
      await uploadToR2(new File([blob], `thumbnail-${file.name}.jpg`, { type: 'image/jpeg' }), thumbnailKey);
      await useFileStore.getState().updateFileThumbnail(file.id, thumbnailKey);
      const { data: refreshedFile, error: fetchError } = await supabase.from('files').select('*').eq('id', file.id).single();
      if (fetchError) throw fetchError;
      if (refreshedFile) {
        const typedFile = { ...refreshedFile as KnowledgeFile, tags: refreshedFile.tags || [], versions: refreshedFile.versions || [] };
        setFile(typedFile);
        if (typedFile.thumbnail_key) {
          const thumbnailUrl = await getPublicUrl(typedFile.thumbnail_key);
          setCurrentThumbnailUrl(thumbnailUrl);
        }
      }
      showToast(t('videoThumbUpdated'));
    } catch (error) {
      console.error('Error capturing video frame:', error);
      showToast(`${t('uploadThumbnailFailedPrefix')}${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsCapturing(false);
    }
  };
  
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfLoading(false);
    setPdfError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('PDF load error:', error);
    setPdfError(error);
    setPdfLoading(false);
  };

  // 监听预览容器尺寸变化，动态计算PDF高度
  useEffect(() => {
    const updatePreviewHeight = () => {
      if (previewContainerRef.current) {
        // 留出底部翻页按钮的空间 (约60px)
        const containerHeight = previewContainerRef.current.clientHeight - 80;
        setPreviewHeight(Math.max(400, containerHeight));
      }
    };

    updatePreviewHeight();
    window.addEventListener('resize', updatePreviewHeight);
    return () => window.removeEventListener('resize', updatePreviewHeight);
  }, []);
  
  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!file.storage_key) return;
    try {
      const url = await getSignedUrl(file.storage_key);
      const response = await fetch(url);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };
  
  useEffect(() => {
    const loadImageUrl = async () => {
      if (file.type === 'image' && file.storage_key) {
        try {
          const url = await getSignedUrl(file.storage_key);
          setImageUrl(url);
        } catch (error) {
          console.error('Error loading image URL:', error);
        }
      }
    };
    loadImageUrl();
  }, [file.type, file.storage_key]);

  useEffect(() => {
    setIsFullImageReady(false);
    if (imageUrl) {
      const img = new Image();
      img.onload = () => setIsFullImageReady(true);
      img.onerror = () => setIsFullImageReady(true);
      img.src = imageUrl;
    }
  }, [imageUrl, file.id]);
  
  useEffect(() => {
    const loadFileUrl = async () => {
      if (!file.storage_key) return;
      try {
        const url = await getSignedUrl(file.storage_key);
        switch (file.type) {
          case 'pdf': setPdfUrl(url); break;
          case 'video': setVideoUrl(url); break;
        }
      } catch (error) {
        console.error('Error loading file URL:', error);
      }
    };
    loadFileUrl();
  }, [file.type, file.storage_key]);

  const handleCaptureScreenshot = async () => {
    try {
      setIsCapturing(true);

      // 通用压缩函数
      const compressImage = async (blob: Blob, maxSizeKB: number = 70): Promise<Blob> => {
        if (blob.size <= maxSizeKB * 1024) return blob;
        const img = document.createElement('img');
        const imgUrl = URL.createObjectURL(blob);
        await new Promise((resolve) => { img.onload = resolve; img.src = imgUrl; });
        let quality = 0.6;
        const compressionCanvas = document.createElement('canvas');
        let imgWidth = img.width, imgHeight = img.height;
        const MAX_DIMENSION = 450;
        if (imgWidth > MAX_DIMENSION || imgHeight > MAX_DIMENSION) {
          if (imgWidth > imgHeight) { imgHeight = Math.round(imgHeight * (MAX_DIMENSION / imgWidth)); imgWidth = MAX_DIMENSION; }
          else { imgWidth = Math.round(imgWidth * (MAX_DIMENSION / imgHeight)); imgHeight = MAX_DIMENSION; }
        }
        compressionCanvas.width = imgWidth; compressionCanvas.height = imgHeight;
        const compressionCtx = compressionCanvas.getContext('2d');
        if (!compressionCtx) { URL.revokeObjectURL(imgUrl); return blob; }
        compressionCtx.drawImage(img, 0, 0, imgWidth, imgHeight);
        URL.revokeObjectURL(imgUrl);
        const attemptCompression = async (currentQuality: number): Promise<Blob> => {
          return new Promise((resolve) => {
            compressionCanvas.toBlob(async (resultBlob) => {
              if (!resultBlob) { resolve(blob); return; }
              if (resultBlob.size > maxSizeKB * 1024 && currentQuality > 0.2) {
                const newQuality = currentQuality * (maxSizeKB * 1024 / resultBlob.size) * 0.9;
                const nextQuality = Math.max(0.2, Math.min(0.9, newQuality));
                resolve(await attemptCompression(nextQuality));
              } else { resolve(resultBlob); }
            }, 'image/jpeg', currentQuality);
          });
        };
        return attemptCompression(quality);
      };

      // 通用上传函数
      const uploadThumbnail = async (blob: Blob) => {
        const finalBlob = await compressImage(blob, 70);
        const uniqueId = Date.now().toString();
        const thumbnailKey = `thumbnails/${file.storage_key}/${uniqueId}-${file.name}`;
        await uploadToR2(new File([finalBlob], `thumbnail-${file.name}.jpg`, { type: 'image/jpeg' }), thumbnailKey);
        await useFileStore.getState().updateFileThumbnail(file.id, thumbnailKey);
        const { data: refreshedFile, error: fetchError } = await supabase.from('files').select('*').eq('id', file.id).single();
        if (fetchError) throw fetchError;
        if (refreshedFile) {
          const typedFile = { ...refreshedFile as KnowledgeFile, tags: refreshedFile.tags || [], versions: refreshedFile.versions || [] };
          setFile(typedFile);
          if (typedFile.thumbnail_key) {
            const thumbnailUrl = await getPublicUrl(typedFile.thumbnail_key);
            setCurrentThumbnailUrl(thumbnailUrl);
          }
        }
        showToast('缩略图已更新');
      };

      // ========== 根据文件类型选择不同截图策略 ==========
      
      if (file.type === 'image') {
        // 图片类型：直接 fetch 图片 blob → canvas 绘制（绕过 CORS）
        if (!file.storage_key) { showToast('文件无存储信息'); return; }
        const url = await getSignedUrl(file.storage_key);
        const response = await fetch(url);
        const imgBlob = await response.blob();
        const imgEl = document.createElement('img');
        const objUrl = URL.createObjectURL(imgBlob);
        await new Promise<void>((resolve, reject) => {
          imgEl.onload = () => resolve();
          imgEl.onerror = () => reject(new Error('图片加载失败'));
          imgEl.src = objUrl;
        });
        const MAX_SIZE = 500;
        const aspectRatio = imgEl.width / imgEl.height;
        let w, h;
        if (aspectRatio > 1) { w = MAX_SIZE; h = Math.round(MAX_SIZE / aspectRatio); }
        else { h = MAX_SIZE; w = Math.round(MAX_SIZE * aspectRatio); }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // 填充白色背景（PNG 透明区域转 JPEG 时保持白底）
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, w, h);
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(imgEl, 0, 0, w, h);
        }
        URL.revokeObjectURL(objUrl);
        const blob = await new Promise<Blob>((resolve) => { canvas.toBlob((b) => { if (b) resolve(b); }, 'image/jpeg', 0.85); });
        await uploadThumbnail(blob);
        
      } else if (file.type === 'video') {
        // 视频类型：复用帧截取（更精准）
        await handleCaptureVideoFrame();
        
      } else if (file.type === 'pdf') {
        // PDF 类型：用 pdfViewerRef + modern-screenshot
        if (!pdfViewerRef.current) { showToast('PDF 尚未加载完成'); return; }
        await new Promise(resolve => setTimeout(resolve, 500));
        const canvas = await domToCanvas(pdfViewerRef.current, {
          scale: 1.5, backgroundColor: '#ffffff'
        });
        const MAX_SIZE = 500;
        const aspectRatio = canvas.width / canvas.height;
        let w, h;
        if (aspectRatio > 1) { w = MAX_SIZE; h = Math.round(MAX_SIZE / aspectRatio); }
        else { h = MAX_SIZE; w = Math.round(MAX_SIZE * aspectRatio); }
        const thumbCanvas = document.createElement('canvas');
        thumbCanvas.width = w; thumbCanvas.height = h;
        const ctx = thumbCanvas.getContext('2d');
        if (ctx) { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h); ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high'; ctx.drawImage(canvas, 0, 0, w, h); }
        const blob = await new Promise<Blob>((resolve) => { thumbCanvas.toBlob((b) => { if (b) resolve(b); }, 'image/jpeg', 0.7); });
        await uploadThumbnail(blob);
        // PDF 需要刷新避免渲染残留
        const tempPdfUrl = pdfUrl;
        setPdfUrl(null);
        setTimeout(() => { setPdfUrl(tempPdfUrl); }, 100);
        
      } else {
        // 代码/Markdown/Office/其他：用 previewAreaRef + modern-screenshot
        if (!previewAreaRef.current) { showToast('预览区域尚未加载'); return; }
        await new Promise(resolve => setTimeout(resolve, 500));
        const canvas = await domToCanvas(previewAreaRef.current, {
          scale: 1.5, backgroundColor: '#ffffff'
        });
        const MAX_SIZE = 500;
        const aspectRatio = canvas.width / canvas.height;
        let w, h;
        if (aspectRatio > 1) { w = MAX_SIZE; h = Math.round(MAX_SIZE / aspectRatio); }
        else { h = MAX_SIZE; w = Math.round(MAX_SIZE * aspectRatio); }
        const thumbCanvas = document.createElement('canvas');
        thumbCanvas.width = w; thumbCanvas.height = h;
        const ctx = thumbCanvas.getContext('2d');
        if (ctx) { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h); ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high'; ctx.drawImage(canvas, 0, 0, w, h); }
        const blob = await new Promise<Blob>((resolve) => { thumbCanvas.toBlob((b) => { if (b) resolve(b); }, 'image/jpeg', 0.7); });
        await uploadThumbnail(blob);
      }
      
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      showToast(`截取预览失败: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsCapturing(false);
    }
  };

  const handleBack = () => { navigate(-1); };
  const handleStarClick = () => { starFile(file.id, !file.starred); };

  const handleDelete = async () => {
    try {
      await deleteFile(file.id);
      
      // 🔄 刷新分类数据
      await fetchCategories();
      
      navigate('/uadmin/files');
    } catch (error) {
      console.error('Error deleting file:', error);
      showToast('删除文件失败，请重试');
    }
  };

  const handleRename = async () => {
    if (!newName.trim() || newName === file.name) {
      setIsEditing(false);
      setNewName(file.name);
      return;
    }
    try {
      await updateFile(file.id, { name: newName.trim() });
      setIsEditing(false);
      showToast('重命名成功');
    } catch (error) {
      console.error('Error renaming file:', error);
      showToast('重命名失败，请重试');
      setNewName(file.name);
    }
  };
  
  const handleCopyUrl = async (type: 'thumbnail' | 'file') => {
    try {
      if (!file.storage_key) return;
      if (type === 'thumbnail') {
        const { data: latestFile, error: fetchError } = await supabase.from('files').select('thumbnail_key').eq('id', file.id).single();
        if (fetchError) throw fetchError;
        if (!latestFile.thumbnail_key) throw new Error('该文件没有缩略图');
        const url = await getPublicUrl(latestFile.thumbnail_key);
        await navigator.clipboard.writeText(url);
        showToast(t('thumbnailUrlCopied'));
      } else {
        const url = await getPublicUrl(file.storage_key);
        await navigator.clipboard.writeText(url);
        showToast(t('fileUrlCopied'));
      }
    } catch (error) {
      console.error('复制URL失败:', error);
      showToast(`复制失败: ${error instanceof Error ? error.message : String(error)}`);
    }
  };
  
  const handleThumbnailUpload = async (imageFile: File) => {
    if (!file.storage_key) {
      showToast(t('cannotUploadThumbMissingStorage'));
      return;
    }
    try {
      setIsUploading(true);
      showToast(t('loadingImage'));
      const compressedFile = await compressImageFile(imageFile, 1200, 0.8);
      const uniqueId = Date.now().toString();
      const thumbnailKey = `thumbnails/${file.storage_key}/${uniqueId}-${file.name}`;
      await uploadToR2(compressedFile, thumbnailKey);
      await useFileStore.getState().updateFileThumbnail(file.id, thumbnailKey);
      const { data: refreshedFile, error: fetchError } = await supabase.from('files').select('*').eq('id', file.id).single();
      if (fetchError) throw fetchError;
      if (refreshedFile) {
        const typedFile = refreshedFile as KnowledgeFile;
        if (!typedFile.tags) typedFile.tags = [];
        if (!typedFile.versions) typedFile.versions = [];
        setFile(typedFile);
        if (typedFile.thumbnail_key) {
          const thumbnailUrl = await getPublicUrl(typedFile.thumbnail_key);
          setCurrentThumbnailUrl(thumbnailUrl);
        }
      }
      setShowUploadModal(false);
      showToast(t('videoThumbUpdated'));
      setTimeout(() => { useFileStore.getState().fetchFiles(); }, 500);
    } catch (error) {
      console.error('上传缩略图出错:', error);
      showToast(`${t('uploadThumbnailFailedPrefix')}${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsUploading(false);
    }
  };
  
  const compressImageFile = async (file: File, maxDimension: number = 1200, quality: number = 0.8): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = document.createElement('img');
        img.src = event.target?.result as string;
        img.onload = () => {
          let width = img.width, height = img.height;
          if (width > height) { if (width > maxDimension) { height = Math.round(height * maxDimension / width); width = maxDimension; } }
          else { if (height > maxDimension) { width = Math.round(width * maxDimension / height); height = maxDimension; } }
          const canvas = document.createElement('canvas');
          canvas.width = width; canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) { reject(new Error('无法获取canvas上下文')); return; }
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (!blob) { reject(new Error('压缩图片失败')); return; }
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        };
        img.onerror = () => { reject(new Error('加载图片失败')); };
      };
      reader.onerror = () => { reject(new Error('读取文件失败')); };
    });
  };
  
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type.startsWith('image/')) { handleThumbnailUpload(droppedFile); }
      else { showToast(t('onlyImageForThumbnail')); }
    }
  };

  // ========== 渲染预览内容 ==========
  const renderFilePreview = () => {
    switch (file.type) {
      case 'image':
        return (
          <div className="flex items-center justify-center w-full h-full">
            {!isFullImageReady ? (
              currentThumbnailUrl ? (
                <img src={currentThumbnailUrl} alt={`${file.name} 缩略图`} className="max-w-full max-h-full object-contain" />
              ) : (
                <div className="text-center">
                  <FileIcon size={48} className="mx-auto text-slate-400" />
                  <p className="mt-2 text-sm text-slate-500">{t('loadingImage')}</p>
                </div>
              )
            ) : null}
            {isFullImageReady && imageUrl && (
              <img src={imageUrl} alt={file.name} className="max-w-full max-h-full object-contain" />
            )}
          </div>
        );
      case 'pdf':
        return (
          <div ref={previewContainerRef} className="flex flex-col w-full h-full bg-slate-50">
            {pdfUrl ? (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* PDF 内容区域 - 铺满整个容器 */}
                <div className="flex-1 overflow-auto flex items-start justify-center p-4">
                  <div ref={pdfViewerRef} className="bg-white shadow-lg rounded-lg overflow-hidden">
                    <Document 
                      file={pdfUrl} 
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={onDocumentLoadError}
                      loading={
                        <div className="flex items-center justify-center" style={{ height: previewHeight, width: '100%' }}>
                          {currentThumbnailUrl ? (
                            <img src={currentThumbnailUrl} alt={`${file.name} 缩略图`} className="max-h-full max-w-full object-contain" />
                          ) : (
                            <div className="flex flex-col items-center">
                              <div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
                              <p className="mt-3 text-sm text-slate-500">{t('loadingPDF')}</p>
                            </div>
                          )}
                        </div>
                      }
                      error={
                        <div className="flex flex-col items-center justify-center p-8" style={{ height: previewHeight }}>
                          <FileIcon size={48} className="text-slate-400 mb-3" />
                          <p className="text-sm text-slate-500 mb-4">PDF 加载失败，请重试</p>
                          <button 
                            onClick={() => { setPdfError(null); setPdfLoading(true); }}
                            className="px-4 py-2 bg-sky-500 text-white text-sm rounded-lg hover:bg-sky-600 transition-colors"
                          >
                            重新加载
                          </button>
                        </div>
                      }
                      className="flex items-center justify-center"
                    >
                      <Page 
                        pageNumber={pageNumber} 
                        renderTextLayer={false} 
                        renderAnnotationLayer={false} 
                        height={previewHeight}
                        className=""
                      />
                    </Document>
                  </div>
                </div>
                {/* 底部翻页控制栏 */}
                {numPages && (
                  <div className="flex-shrink-0 flex items-center justify-center py-3 px-4 bg-white border-t border-slate-200">
                    <div className="flex items-center space-x-3">
                      <button 
                        onClick={() => setPageNumber(p => Math.max(1, p - 1))} 
                        disabled={pageNumber <= 1}
                        className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 transition-colors"
                      >
                        上一页
                      </button>
                      <span className="text-sm font-medium text-slate-700 bg-slate-100 px-4 py-2 rounded-lg min-w-[80px] text-center">
                        {pageNumber} / {numPages}
                      </span>
                      <button 
                        onClick={() => setPageNumber(p => Math.min(numPages, p + 1))} 
                        disabled={pageNumber >= numPages}
                        className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 transition-colors"
                      >
                        下一页
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                {currentThumbnailUrl ? (
                  <img src={currentThumbnailUrl} alt={`${file.name} 缩略图`} className="max-h-[400px] object-contain" />
                ) : (
                  <div className="text-center">
                    <div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="mt-3 text-sm text-slate-500">{t('loadingPDF')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      case 'video':
        return (
          <div className="flex flex-col items-center justify-center w-full h-full bg-slate-900">
            {videoUrl ? (
              <div className="w-full h-full flex items-center justify-center">
                <ReactPlayer ref={videoRef} url={videoUrl} crossOrigin="anonymous" controls width="100%" height="100%"
                  config={{ file: { attributes: { controlsList: 'nodownload', crossOrigin: 'anonymous' } } }}
                />
              </div>
            ) : (
              <div className="text-center py-10">
                {currentThumbnailUrl ? (
                  <img src={currentThumbnailUrl} alt={`${file.name} 缩略图`} className="max-h-[280px] object-contain mx-auto" />
                ) : (
                  <>
                    <FileIcon size={48} className="mx-auto text-slate-400" />
                    <p className="mt-2 text-sm text-slate-400">{t('loadingVideo')}</p>
                  </>
                )}
              </div>
            )}
          </div>
        );
      case 'code':
        const language = getLanguageFromExtension(file.name);
        const isMarkdown = isMarkdownFile(file.name);
        if (codeLoading) {
          return (
            <div className="flex items-center justify-center w-full h-full bg-slate-100">
              <div className="text-center">
                <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                <p className="mt-2 text-sm text-slate-500">{t('loadingCode')}</p>
              </div>
            </div>
          );
        }
        if (!codeContent) {
          return (
            <div className="flex items-center justify-center w-full h-full bg-slate-100">
              <div className="text-center">
                <FileIcon size={48} className="mx-auto text-slate-400" />
                <p className="mt-2 text-sm text-slate-500">{t('loadCodeFailed')}</p>
              </div>
            </div>
          );
        }
        // Markdown 使用浅色背景，便于阅读
        if (isMarkdown) {
          return (
            <div className="w-full h-full bg-white dark:bg-gray-900 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-4 py-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{file.name}</span>
                <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded">markdown</span>
              </div>
              <div className="flex-1 overflow-auto p-6">
                <div className="prose prose-sm md:prose-base max-w-none dark:prose-invert prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-strong:text-gray-900 dark:prose-strong:text-white prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:text-pink-600 dark:prose-code:text-pink-400 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-table:border-collapse prose-th:bg-gray-100 dark:prose-th:bg-gray-800 prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:px-3 prose-th:py-2 prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 prose-td:px-3 prose-td:py-2">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ node, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        const inline = !match;
                        return !inline ? (
                          <SyntaxHighlighter style={oneDark} language={match ? match[1] : 'text'} PreTag="div">
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        ) : (<code className={className} {...props}>{children}</code>);
                      }
                    }}
                  >
                    {codeContent}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          );
        }
        // 代码文件使用深色背景，便于语法高亮
        return (
          <div className="w-full h-full bg-slate-900 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
              <span className="text-sm text-slate-300 font-mono">{file.name}</span>
              <span className="text-xs text-slate-500 bg-slate-700 px-2 py-0.5 rounded">{language}</span>
            </div>
            <div className="flex-1 overflow-auto">
              <SyntaxHighlighter language={language} style={oneDark} showLineNumbers wrapLines customStyle={{ margin: 0, borderRadius: 0, minHeight: '100%' }}>
                {codeContent}
              </SyntaxHighlighter>
            </div>
          </div>
        );
      default:
        const isOfficeFile = ['document', 'spreadsheet', 'presentation'].includes(file.type);
        if (isOfficeFile && officeViewerUrl && !officePreviewError) {
          return (
            <div className="flex flex-col items-center justify-center w-full h-full bg-slate-50 overflow-hidden">
              <div className="w-full h-full relative">
                <iframe src={officeViewerUrl} className="absolute inset-0 w-full h-full border-none" title={`${file.name} 预览`} loading="lazy" onError={() => setOfficePreviewError(true)}></iframe>
              </div>
            </div>
          );
        }
        return (
          <div className="flex flex-col items-center justify-center w-full h-full bg-slate-50">
            <div className="flex flex-col items-center justify-center py-16">
              <div className="flex items-center justify-center w-24 h-24 rounded-2xl mb-4"
                style={{ backgroundColor: file.type === 'document' ? '#e0f2fe' : file.type === 'spreadsheet' ? '#ecfdf5' : file.type === 'presentation' ? '#f5f3ff' : '#f1f5f9' }}>
                <FileIcon size={48} className={`${file.type === 'document' ? 'text-blue-500' : file.type === 'spreadsheet' ? 'text-emerald-500' : file.type === 'presentation' ? 'text-purple-500' : 'text-slate-500'}`} />
              </div>
              {file.thumbnail_key && currentThumbnailUrl && (
                <div className="mt-2 mb-6 max-w-md">
                  <img src={currentThumbnailUrl} alt={`${file.name} 缩略图`} className="max-w-full max-h-[200px] object-contain rounded-xl shadow-sm" />
                </div>
              )}
              <p className="text-sm text-slate-500 mb-4">{t('noPreviewAvailable')}</p>
              <button onClick={(e) => handleDownload(e)} className="px-5 py-2.5 bg-sky-500 text-white rounded-xl hover:bg-sky-600 transition-colors flex items-center shadow-sm">
                <Download size={18} className="mr-2" />
                下载文件
              </button>
            </div>
          </div>
        );
    }
  };

  // ========== 全屏预览渲染 ==========
  const renderFullscreenPreview = () => {
    switch (file.type) {
      case 'image':
        return imageUrl ? <img src={imageUrl} alt={file.name} className="max-w-full max-h-full object-contain" /> : null;
      case 'pdf':
        return pdfUrl ? (
          <div className="w-full h-full flex flex-col items-center">
            <div className="flex-1 overflow-auto flex items-center justify-center">
              <Document file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess} className="flex items-center justify-center">
                <Page pageNumber={pageNumber} renderTextLayer={false} renderAnnotationLayer={false} className="shadow-lg" height={window.innerHeight - 150} />
              </Document>
            </div>
            {numPages && (
              <div className="flex items-center justify-center py-3 space-x-4 bg-slate-900/50 w-full">
                <button onClick={() => setPageNumber(p => Math.max(1, p - 1))} disabled={pageNumber <= 1}
                  className="px-4 py-2 text-sm text-white bg-slate-700 rounded-lg disabled:opacity-50 hover:bg-slate-600">上一页</button>
                <span className="text-sm text-white">{pageNumber} / {numPages}</span>
                <button onClick={() => setPageNumber(p => Math.min(numPages, p + 1))} disabled={pageNumber >= numPages}
                  className="px-4 py-2 text-sm text-white bg-slate-700 rounded-lg disabled:opacity-50 hover:bg-slate-600">下一页</button>
              </div>
            )}
          </div>
        ) : null;
      case 'video':
        return videoUrl ? (
          <div className="w-full max-w-6xl aspect-video">
            <ReactPlayer url={videoUrl} controls width="100%" height="100%" config={{ file: { attributes: { controlsList: 'nodownload', crossOrigin: 'anonymous' } } }} />
          </div>
        ) : null;
      case 'code':
        const language = getLanguageFromExtension(file.name);
        const isMarkdown = isMarkdownFile(file.name);
        if (!codeContent) return null;
        return (
          <div className="w-full max-w-6xl h-full bg-slate-900 rounded-xl overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
              <span className="text-sm text-slate-300 font-mono">{file.name}</span>
              <span className="text-xs text-slate-500 bg-slate-700 px-2 py-0.5 rounded">{language}</span>
            </div>
            <div className="flex-1 overflow-auto">
              {isMarkdown ? (
                <div className="p-6 prose prose-invert prose-sm max-w-none">
                  <ReactMarkdown components={{
                    code({ node, className, children, ...props }) {
                      const match = /language-(\w+)/.exec(className || '');
                      const inline = !match;
                      return !inline ? (
                        <SyntaxHighlighter style={oneDark} language={match ? match[1] : 'text'} PreTag="div">{String(children).replace(/\n$/, '')}</SyntaxHighlighter>
                      ) : (<code className={className} {...props}>{children}</code>);
                    }
                  }}>{codeContent}</ReactMarkdown>
                </div>
              ) : (
                <SyntaxHighlighter language={language} style={oneDark} showLineNumbers wrapLines customStyle={{ margin: 0, minHeight: '100%' }}>{codeContent}</SyntaxHighlighter>
              )}
            </div>
          </div>
        );
      default:
        if (officeViewerUrl && !officePreviewError) {
          return (
            <div className="w-full max-w-6xl h-full bg-white rounded-xl overflow-hidden">
              <iframe src={officeViewerUrl} className="w-full h-full border-none" title={`${file.name} 预览`}></iframe>
            </div>
          );
        }
        return (
          <div className="text-center text-white">
            <FileIcon size={64} className="mx-auto mb-4 text-slate-400" />
            <p className="text-slate-400">{t('noPreviewAvailable')}</p>
          </div>
        );
    }
  };

  // ========== 主渲染 - Apple 风格重新设计 ==========
  return (
    <div className="flex flex-col h-full bg-slate-50">
      {/* 顶部导航栏 - 简洁风格 */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200 shadow-sm">
        {/* 左侧：返回 + 文件名 */}
        <div className="flex items-center min-w-0 flex-1">
          <button onClick={handleBack} className="p-2 -ml-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors" title="返回">
            <ArrowLeft size={20} />
          </button>
          <div className="ml-2 min-w-0 flex-1">
            {isEditing && isDesktop ? (
              <div className="flex items-center">
                <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                  className="px-3 py-1.5 text-base font-medium text-slate-900 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  autoFocus />
                <button onClick={handleRename} className="p-2 ml-2 text-sky-600 hover:bg-sky-50 rounded-lg" title="保存"><Check size={18} /></button>
                <button onClick={() => { setIsEditing(false); setNewName(file.name); }} className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg" title="取消"><X size={18} /></button>
              </div>
            ) : (
              <div className="flex flex-col min-w-0">
                <h1 className="font-medium text-slate-800 truncate text-sm md:text-base" title={file.name}>{file.name}</h1>
                {/* 面包屑导航 */}
                <div
                  className="flex items-center text-xs text-slate-500 mt-0.5 overflow-hidden max-w-[min(70vw,760px)]"
                  title={primaryFolderPathLabel || undefined}
                >
                  <button onClick={() => navigate('/uadmin/categories')} className="hover:text-sky-600 transition-colors flex-shrink-0">文件夹</button>
                  {fileLocationLoading && primaryCategoryPath.length === 0 ? (
                    <span className="ml-1 truncate text-slate-400">定位中...</span>
                  ) : primaryCategoryPath.length > 0 ? (
                    <>
                      {primaryCategoryPath.length > topBreadcrumbPath.length && (
                        <span className="flex items-center flex-shrink-0">
                          <ChevronRight size={12} className="mx-1 text-slate-400" />
                          <span className="text-slate-400">...</span>
                        </span>
                      )}
                      {topBreadcrumbPath.map((node) => (
                        <span key={node.id} className="flex items-center flex-shrink-0">
                          <ChevronRight size={12} className="mx-1 text-slate-400" />
                          <button onClick={() => navigate(`/uadmin/categories/${node.id}`)} className="hover:text-sky-600 transition-colors truncate max-w-[180px] md:max-w-[240px]">{node.name}</button>
                        </span>
                      ))}
                      {fileLocationPaths.length > 1 && (
                        <span className="ml-1 flex-shrink-0 text-slate-400">等 {fileLocationPaths.length} 个位置</span>
                      )}
                    </>
                  ) : (
                    <span className="ml-1 truncate text-slate-400">未归类</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* 右侧：主要操作按钮 */}
        <div className="flex items-center space-x-1 ml-4">
          {/* 收藏 */}
          <button onClick={handleStarClick}
            className={cn("p-2 rounded-xl transition-colors", file.starred ? "text-amber-500 bg-amber-50" : "text-slate-400 hover:text-slate-600 hover:bg-slate-100")}
            title={file.starred ? '取消收藏' : '收藏'}>
            <Star size={20} fill={file.starred ? 'currentColor' : 'none'} />
          </button>
          
          {/* 下载 */}
          <button onClick={(e) => handleDownload(e)} className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors" title="下载">
            <Download size={20} />
          </button>

          <SourceParseButton
            fileId={file.id}
            fileName={file.name}
            storageKey={file.storage_key}
            compact
            className="inline-flex items-center rounded-xl bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
          />
          
          {/* 全屏 */}
          <button onClick={() => setIsFullscreen(true)} className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors" title="全屏预览">
            <Maximize2 size={20} />
          </button>
          
          {/* 信息面板切换 */}
          <button onClick={() => setShowInfoPanel(!showInfoPanel)}
            className={cn("p-2 rounded-xl transition-colors", showInfoPanel ? "text-sky-600 bg-sky-50" : "text-slate-500 hover:text-slate-700 hover:bg-slate-100")}
            title={showInfoPanel ? '隐藏详情' : '显示详情'}>
            {showInfoPanel ? <PanelRightClose size={20} /> : <PanelRightOpen size={20} />}
          </button>
          
          {/* 更多操作 */}
          <div className="relative">
            <button onClick={() => setMenuOpen(!menuOpen)} className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors">
              <MoreHorizontal size={20} />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-xl shadow-lg border border-slate-200 py-1 w-48 overflow-hidden">
                  <button className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center"
                    onClick={() => { setMenuOpen(false); if (isDesktop) setIsEditing(true); else { setNewName(file.name); setShowRenameModal(true); } }}>
                    <Edit3 size={16} className="mr-3 text-slate-400" />重命名
                  </button>
                  <button className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center"
                    onClick={() => { handleCopyUrl('file'); setMenuOpen(false); }}>
                    <Link2 size={16} className="mr-3 text-slate-400" />复制文件链接
                  </button>
                  {file.thumbnail_key && (
                    <button className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center"
                      onClick={() => { handleCopyUrl('thumbnail'); setMenuOpen(false); }}>
                      <Copy size={16} className="mr-3 text-slate-400" />复制缩略图链接
                    </button>
                  )}
                  <div className="my-1 border-t border-slate-100" />
                  <button className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center"
                    onClick={() => { setShowUploadModal(true); setMenuOpen(false); }}>
                    <ImageIcon size={16} className="mr-3 text-slate-400" />{file.thumbnail_key ? '更新缩略图' : '上传缩略图'}
                  </button>
                  {file.type === 'video' && (
                    <button className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center"
                      onClick={async () => { await handleCaptureVideoFrame(); setMenuOpen(false); }} disabled={isCapturing}>
                      <Camera size={16} className="mr-3 text-slate-400" />截取当前帧
                    </button>
                  )}
                  <button className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center"
                    onClick={async () => { await handleCaptureScreenshot(); setMenuOpen(false); }} disabled={isCapturing}>
                    <Camera size={16} className="mr-3 text-slate-400" />截取预览截图
                  </button>
                  <button className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center"
                    onClick={() => { setShowPermissionModal(true); setMenuOpen(false); }}>
                    <Shield size={16} className="mr-3 text-slate-400" />权限设置
                  </button>
                  <div className="px-3 py-2">
                    <SourceParseButton
                      fileId={file.id}
                      fileName={file.name}
                      storageKey={file.storage_key}
                      compact
                      className="w-full inline-flex items-center justify-center rounded-lg bg-indigo-50 px-3 py-2 text-sm text-indigo-700 hover:bg-indigo-100"
                    />
                  </div>
                  <div className="my-1 border-t border-slate-100" />
                  <button className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center"
                    onClick={() => { setShowDeleteConfirm(true); setMenuOpen(false); }}>
                    <Trash2 size={16} className="mr-3" />删除文件
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 主内容区域 */}
      <div className="flex-1 flex overflow-hidden">
        {/* 预览区域 */}
        <div className={cn("flex-1 overflow-hidden transition-all duration-300", showInfoPanel && isDesktop ? "mr-80" : "")}>
          <div className="h-full p-4">
            <div ref={previewAreaRef} className="h-full bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              {renderFilePreview()}
            </div>
          </div>
        </div>
        
        {/* 右侧信息面板 - 桌面端固定，移动端滑出 */}
        {showInfoPanel && (
          <>
            {/* 移动端遮罩 */}
            {!isDesktop && <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setShowInfoPanel(false)} />}
            
            <div className={cn(
              "bg-white border-l border-slate-200 overflow-y-auto",
              isDesktop ? "fixed right-0 top-[57px] bottom-0 w-80" : "fixed right-0 top-0 bottom-0 w-80 z-50 shadow-xl"
            )}>
              {/* 移动端关闭按钮 */}
              {!isDesktop && (
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900">文件详情</h3>
                  <button onClick={() => setShowInfoPanel(false)} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg">
                    <X size={18} />
                  </button>
                </div>
              )}
              
              <div className="p-4 space-y-4">
                {/* 缩略图预览 */}
                {currentThumbnailUrl && (
                  <div className="bg-slate-100 rounded-xl p-3">
                    <img src={currentThumbnailUrl} alt="缩略图" className="w-full h-32 object-contain rounded-lg" />
                  </div>
                )}
                
                {/* 文件信息卡片 */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center">
                    <Info size={16} className="mr-2 text-sky-500" />
                    文件信息
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">类型</span>
                      <span className="text-sm font-medium text-slate-700 flex items-center">
                        <FileIcon size={14} className="mr-1.5 text-slate-400" />
                        {file.type.charAt(0).toUpperCase() + file.type.slice(1)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">大小</span>
                      <span className="text-sm font-medium text-slate-700">{formatFileSize(file.size)}</span>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-sm text-slate-500 flex-shrink-0">所在文件夹</span>
                      {fileLocationLoading && !primaryCategory ? (
                        <span className="text-sm font-medium text-slate-400">定位中...</span>
                      ) : primaryCategory ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/uadmin/categories/${primaryCategory.id}`)}
                          className="text-right text-sm font-medium text-sky-600 hover:text-sky-700 break-words leading-5"
                          title={primaryFolderPathLabel}
                        >
                          {primaryCategory.name}
                        </button>
                      ) : (
                        <span className="text-sm font-medium text-slate-700">未归类</span>
                      )}
                    </div>
                    <div className="space-y-1">
                      <span className="text-sm text-slate-500">当前路径</span>
                      <span
                        className="block text-sm font-medium text-slate-700 break-words leading-5"
                        title={primaryFolderPathLabel ? currentFilePathLabel : `全部文件 / ${file.name}`}
                      >
                        {fileLocationLoading && !primaryCategory
                          ? '定位中...'
                          : primaryFolderPathLabel
                            ? currentFilePathLabel
                            : `全部文件 / ${file.name}`}
                      </span>
                    </div>
                    {fileLocationPaths.length > 1 && (
                      <div className="text-right text-xs text-slate-400">
                        另有 {fileLocationPaths.length - 1} 个位置
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">创建时间</span>
                      <span className="text-sm font-medium text-slate-700">{dayjs(file.createdAt).format('YYYY-MM-DD')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">修改时间</span>
                      <span className="text-sm font-medium text-slate-700">{dayjs(file.updatedAt).format('YYYY-MM-DD HH:mm')}</span>
                    </div>
                  </div>
                </div>
                
                {/* 权限信息卡片 */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-900 flex items-center">
                      <Shield size={16} className="mr-2 text-sky-500" />
                      权限设置
                    </h3>
                    <button onClick={() => setShowPermissionModal(true)} className="text-xs text-sky-600 hover:text-sky-700 font-medium">
                      修改
                    </button>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">访问级别</span>
                      <span className={cn("px-2 py-0.5 text-xs rounded-full font-medium",
                        file.min_permission_level ? PERMISSION_COLORS[file.min_permission_level] : "bg-slate-200 text-slate-600")}>
                        {file.min_permission_level ? PERMISSION_LABELS[file.min_permission_level] : '基础'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">公开状态</span>
                      <span className={cn("px-2 py-0.5 text-xs rounded-full font-medium",
                        file.owner_only ? "bg-purple-100 text-purple-700" : (file.is_public ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"))}>
                        {file.owner_only ? '私密' : (file.is_public ? '公开' : '私有')}
                      </span>
                    </div>
                    {!file.owner_only && file.is_public && file.public_expires_at && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-500">过期时间</span>
                        <span className="text-sm text-slate-700">{dayjs(file.public_expires_at).format('YYYY-MM-DD')}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* 标签 */}
                {file.tags && file.tags.length > 0 && (
                  <div className="bg-slate-50 rounded-xl p-4">
                    <h3 className="text-xs font-semibold text-slate-900 mb-2">标签</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {file.tags.map((tag) => (
                        <span key={tag.id} className="inline-flex items-center px-2 py-0.5 text-xs rounded-full"
                          style={{ backgroundColor: `${tag.color}20`, color: tag.color }}>
                          <span className="w-1.5 h-1.5 mr-1 rounded-full" style={{ backgroundColor: tag.color }}></span>
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* 快捷操作 */}
                <div className="bg-slate-50 rounded-xl p-3">
                  <h3 className="text-xs font-semibold text-slate-900 mb-2">快捷操作</h3>
                  <div className="space-y-1.5">
                    {/* 收藏按钮 */}
                    <button onClick={handleStarClick}
                      className={cn("w-full flex items-center justify-center px-3 py-2 text-sm rounded-lg transition-colors",
                        file.starred ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-slate-200 text-slate-700 hover:bg-slate-300")}>
                      <Star size={15} className="mr-1.5" fill={file.starred ? 'currentColor' : 'none'} />
                      {file.starred ? '取消收藏' : '添加收藏'}
                    </button>
                    <button onClick={(e) => handleDownload(e)}
                      className="w-full flex items-center justify-center px-3 py-2 bg-sky-500 text-white text-sm rounded-lg hover:bg-sky-600 transition-colors">
                      <Download size={15} className="mr-1.5" />下载文件
                    </button>
                    <button onClick={() => handleCopyUrl('file')}
                      className="w-full flex items-center justify-center px-3 py-2 bg-slate-200 text-slate-700 text-sm rounded-lg hover:bg-slate-300 transition-colors">
                      <Link2 size={15} className="mr-1.5" />复制文件链接
                    </button>
                    <SourceParseButton fileId={file.id} fileName={file.name} storageKey={file.storage_key} />
                    {file.thumbnail_key && (
                      <button onClick={() => handleCopyUrl('thumbnail')}
                        className="w-full flex items-center justify-center px-3 py-2 bg-slate-200 text-slate-700 text-sm rounded-lg hover:bg-slate-300 transition-colors">
                        <Copy size={15} className="mr-1.5" />复制缩略图链接
                      </button>
                    )}
                  </div>
                </div>
                
                {/* 文件管理 */}
                <div className="bg-slate-50 rounded-xl p-3">
                  <h3 className="text-xs font-semibold text-slate-900 mb-2">文件管理</h3>
                  <div className="space-y-1.5">
                    <button onClick={() => { if (isDesktop) setIsEditing(true); else { setNewName(file.name); setShowRenameModal(true); } }}
                      className="w-full flex items-center justify-center px-3 py-2 bg-slate-200 text-slate-700 text-sm rounded-lg hover:bg-slate-300 transition-colors">
                      <Edit3 size={15} className="mr-1.5" />重命名
                    </button>
                    <button onClick={() => setShowPermissionModal(true)}
                      className="w-full flex items-center justify-center px-3 py-2 bg-slate-200 text-slate-700 text-sm rounded-lg hover:bg-slate-300 transition-colors">
                      <Shield size={15} className="mr-1.5" />权限设置
                    </button>
                    <button onClick={() => setShowDeleteConfirm(true)}
                      className="w-full flex items-center justify-center px-3 py-2 bg-red-50 text-red-600 text-sm rounded-lg hover:bg-red-100 transition-colors">
                      <Trash2 size={15} className="mr-1.5" />删除文件
                    </button>
                  </div>
                </div>
                
                {/* 缩略图管理 */}
                <div className="bg-slate-50 rounded-xl p-3">
                  <h3 className="text-xs font-semibold text-slate-900 mb-2 flex items-center">
                    <ImageIcon size={14} className="mr-1.5 text-sky-500" />
                    缩略图管理
                  </h3>
                  <div className="space-y-1.5">
                    <button onClick={() => setShowUploadModal(true)}
                      className="w-full flex items-center justify-center px-3 py-2 bg-slate-200 text-slate-700 text-sm rounded-lg hover:bg-slate-300 transition-colors">
                      <Upload size={15} className="mr-1.5" />
                      {file.thumbnail_key ? '更新缩略图' : '上传缩略图'}
                    </button>
                    {file.type === 'video' && (
                      <button onClick={handleCaptureVideoFrame} disabled={isCapturing}
                        className={cn("w-full flex items-center justify-center px-3 py-2 text-sm rounded-lg transition-colors",
                          isCapturing ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-amber-50 text-amber-700 hover:bg-amber-100")}>
                        <Camera size={15} className="mr-1.5" />
                        {isCapturing ? '截取中...' : '截取当前帧'}
                      </button>
                    )}
                    <button onClick={handleCaptureScreenshot} disabled={isCapturing}
                      className={cn("w-full flex items-center justify-center px-3 py-2 text-sm rounded-lg transition-colors",
                        isCapturing ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-amber-50 text-amber-700 hover:bg-amber-100")}>
                      <Camera size={15} className="mr-1.5" />
                      {isCapturing ? '截取中...' : '截取预览截图'}
                    </button>
                  </div>
                </div>
                
                {/* 版本历史 */}
                {file.versions && file.versions.length > 0 && (
                  <div className="bg-slate-50 rounded-xl p-3">
                    <h3 className="text-xs font-semibold text-slate-900 mb-2">版本历史</h3>
                    <div className="space-y-1.5">
                      {file.versions.map((version) => (
                        <div key={version.id} className="flex items-center justify-between p-2 bg-white rounded-lg">
                          <div>
                            <div className="text-xs font-medium text-slate-700">{dayjs(version.createdAt).format('YYYY-MM-DD')}</div>
                            {version.note && <div className="text-xs text-slate-500">{version.note}</div>}
                          </div>
                          <button className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded">
                            <Download size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* 删除确认弹窗 */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm mx-4 p-6 bg-white rounded-2xl shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">删除文件</h3>
            <p className="text-sm text-slate-600 mb-6">确定要删除「{file.name}」吗？此操作无法撤销。</p>
            <div className="flex space-x-3">
              <button onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors">
                取消
              </button>
              <button onClick={handleDelete}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 rounded-xl hover:bg-red-600 transition-colors">
                删除
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 上传缩略图弹窗 */}
      {showUploadModal && (
        <ThumbnailUploadModal
          file={file}
          currentThumbnailUrl={currentThumbnailUrl}
          isUploading={isUploading}
          onClose={() => setShowUploadModal(false)}
          onUpload={handleThumbnailUpload}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          t={t}
        />
      )}

      {/* 移动端重命名弹窗 */}
      {!isDesktop && showRenameModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm mx-4 p-6 bg-white rounded-2xl shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">重命名文件</h3>
            <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
              className="w-full px-4 py-3 text-base border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              placeholder="输入新名称" autoFocus />
            <div className="flex space-x-3 mt-4">
              <button onClick={() => setShowRenameModal(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors">
                取消
              </button>
              <button onClick={async () => { await handleRename(); setShowRenameModal(false); }}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-sky-500 rounded-xl hover:bg-sky-600 transition-colors">
                保存
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 权限设置弹窗 */}
      {showPermissionModal && (
        <FilePermissionModal file={file} onClose={() => setShowPermissionModal(false)}
          onUpdate={() => {
            useFileStore.getState().fetchFiles();
            const updatedFiles = useFileStore.getState().files;
            const updatedFile = updatedFiles.find(f => f.id === file.id);
            if (updatedFile) setFile(updatedFile);
          }} />
      )}
      
      {/* 全屏预览 */}
      {isFullscreen && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 bg-slate-900/80">
            <div className="flex items-center text-white min-w-0">
              <FileIcon size={20} className="mr-2 flex-shrink-0" />
              <span className="font-medium truncate">{file.name}</span>
            </div>
            <div className="flex items-center space-x-2">
              <button onClick={(e) => handleDownload(e)} className="p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors" title="下载">
                <Download size={20} />
              </button>
              <button onClick={() => setIsFullscreen(false)} className="p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors" title="退出全屏">
                <Minimize2 size={20} />
              </button>
              <button onClick={() => setIsFullscreen(false)} className="p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors" title="关闭">
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto flex items-center justify-center p-4">
            {renderFullscreenPreview()}
          </div>
        </div>
      )}
    </div>
  );
};

// ========== 上传缩略图弹窗（支持剪贴板粘贴） ==========
interface ThumbnailUploadModalProps {
  file: KnowledgeFile;
  currentThumbnailUrl: string | null;
  isUploading: boolean;
  onClose: () => void;
  onUpload: (file: File) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  t: (key: string) => string;
}

const ThumbnailUploadModal: React.FC<ThumbnailUploadModalProps> = ({
  file, currentThumbnailUrl, isUploading, onClose, onUpload, onDragOver, onDrop, t
}) => {
  const [pastePreview, setPastePreview] = useState<string | null>(null);

  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          e.preventDefault();
          const blob = items[i].getAsFile();
          if (blob) {
            // 显示粘贴预览
            const previewUrl = URL.createObjectURL(blob);
            setPastePreview(previewUrl);
            // 创建 File 对象并上传
            const pastedFile = new File([blob], `clipboard-${Date.now()}.png`, { type: blob.type });
            onUpload(pastedFile);
            // 清理预览
            setTimeout(() => {
              URL.revokeObjectURL(previewUrl);
              setPastePreview(null);
            }, 3000);
          }
          return;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [onUpload]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onDragOver={onDragOver} onDrop={onDrop}>
      <div className="w-full max-w-md mx-4 p-6 bg-white rounded-2xl shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-slate-900">上传缩略图</h3>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        
        {file.thumbnail_key && currentThumbnailUrl && (
          <div className="mb-4 p-3 bg-slate-50 rounded-xl">
            <p className="text-xs text-slate-500 mb-2">当前缩略图</p>
            <img src={currentThumbnailUrl} alt="当前缩略图" className="max-h-[120px] mx-auto object-contain rounded-lg" />
          </div>
        )}

        {/* 粘贴预览 */}
        {pastePreview && (
          <div className="mb-4 p-3 bg-sky-50 rounded-xl border border-sky-200 animate-pulse">
            <p className="text-xs text-sky-600 mb-2 font-medium">正在上传粘贴的图片...</p>
            <img src={pastePreview} alt="粘贴预览" className="max-h-[120px] mx-auto object-contain rounded-lg" />
          </div>
        )}
        
        <label className="block w-full p-6 text-center border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-sky-400 hover:bg-sky-50/50 transition-colors"
          onDragOver={onDragOver} onDrop={onDrop}>
          <input type="file" accept="image/*" className="hidden"
            onChange={(e) => {
              const selectedFile = e.target.files?.[0];
              if (selectedFile && selectedFile.type.startsWith('image/')) { onUpload(selectedFile); }
              else { showToast(t('pleaseSelectValidImage')); }
            }} />
          <Upload className="w-10 h-10 mx-auto mb-3 text-slate-400" />
          <span className="text-sm text-slate-600">点击选择或拖拽图片到此处</span>
          <span className="block mt-1 text-xs text-slate-400">支持 JPG、PNG、GIF 格式</span>
        </label>

        {/* 粘贴提示 */}
        <div className="mt-3 flex items-center justify-center gap-2 text-xs text-slate-400">
          <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-mono">Ctrl</kbd>
          <span>+</span>
          <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-mono">V</kbd>
          <span>粘贴剪贴板图片</span>
        </div>
        
        <div className="flex justify-end mt-4">
          <button onClick={onClose} disabled={isUploading}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">
            取消
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileDetailsViewer;
